/**
 * Configuration Service
 * 
 * This service provides access to the configurable taxonomy and AI prompts.
 * It serves as a simple interface for managing document categories and AI processing.
 * 
 * Features:
 * - Access to document taxonomy configuration
 * - Access to AI processing prompts
 * - Simple getter methods for configuration data
 * - Fallback handling for missing configuration
 */

import { DOCUMENT_TAXONOMY, getCategoryById, getCategoryIds, getCategoriesForAI } from '../config/documentTaxonomy';
import { AI_PROMPTS, getPrompt, getPromptTypes } from '../config/aiPrompts';

export const configService = {
  /**
   * Get the complete document taxonomy
   * @returns {Object} The document taxonomy configuration
   */
  getTaxonomy() {
    return DOCUMENT_TAXONOMY;
  },
  
  /**
   * Get all document categories
   * @returns {Array} Array of category objects
   */
  getCategories() {
    return DOCUMENT_TAXONOMY.categories;
  },
  
  /**
   * Get a specific category by ID
   * @param {string} categoryId - The category ID to find
   * @returns {Object|null} The category object or null if not found
   */
  getCategory(categoryId) {
    return getCategoryById(categoryId);
  },
  
  /**
   * Get all category IDs
   * @returns {Array} Array of category IDs
   */
  getCategoryIds() {
    return getCategoryIds();
  },
  
  /**
   * Get categories formatted for AI processing
   * @returns {Array} Array of categories for AI prompts
   */
  getCategoriesForAI() {
    return getCategoriesForAI();
  },
  
  /**
   * Get a specific AI prompt by type
   * @param {string} promptType - The type of prompt to retrieve
   * @returns {string} The prompt text
   */
  getPrompt(promptType) {
    return getPrompt(promptType);
  },
  
  /**
   * Get all available AI prompt types
   * @returns {Array} Array of available prompt types
   */
  getPromptTypes() {
    return getPromptTypes();
  },
  
  /**
   * Get all AI prompts
   * @returns {Object} Object containing all AI prompts
   */
  getPrompts() {
    return AI_PROMPTS;
  },
  
  /**
   * Check if a category ID is valid
   * @param {string} categoryId - The category ID to validate
   * @returns {boolean} True if the category ID is valid
   */
  isValidCategory(categoryId) {
    return getCategoryById(categoryId) !== null;
  },
  
  /**
   * Get the default category (uncategorized)
   * @returns {Object} The uncategorized category object
   */
  getDefaultCategory() {
    return getCategoryById('uncategorized');
  },
  
  /**
   * Get categories excluding the uncategorized category
   * @returns {Array} Array of category objects (excluding uncategorized)
   */
  getCategorizedCategories() {
    return DOCUMENT_TAXONOMY.categories.filter(cat => cat.id !== 'uncategorized');
  },
  
  /**
   * Validate the configuration
   * @returns {Object} Validation result with errors array
   */
  validateConfig() {
    const errors = [];
    
    // Check if categories exist
    if (!DOCUMENT_TAXONOMY.categories || DOCUMENT_TAXONOMY.categories.length === 0) {
      errors.push('No categories defined in taxonomy');
    }
    
    // Check if uncategorized category exists
    if (!getCategoryById('uncategorized')) {
      errors.push('Uncategorized category is required but not found');
    }
    
    // Check if AI prompts exist
    if (!AI_PROMPTS || Object.keys(AI_PROMPTS).length === 0) {
      errors.push('No AI prompts defined');
    }
    
    // Validate category structure
    DOCUMENT_TAXONOMY.categories.forEach((cat, index) => {
      if (!cat.id) errors.push(`Category at index ${index} missing id`);
      if (!cat.name) errors.push(`Category at index ${index} missing name`);
      if (!cat.icon) errors.push(`Category at index ${index} missing icon`);
      if (!cat.description) errors.push(`Category at index ${index} missing description`);
    });
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
}; 